import { useEffect, useMemo, useState } from 'react';
import { Table, OverlayTrigger, Tooltip } from 'react-bootstrap';

//components
import Button from '../../../../components/CustomButton/CustomButton';

//elements
import CustomMultiSelect from '../../../../elements/CustomMultiSelect';

//assets
import Delete from '../../../../assets/img/deleting.svg';

const PermissionGroupMembers = ({
    t,
    locale,
    role,
    members,
    employeeOptions,
    membershipByUserId,
    departmentLabels = {},
    groupId,
    onAdd,
    onRemove,
    onReapply,
}) => {
    const [pendingIds, setPendingIds] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);

    const assignedIds = useMemo(() => new Set(members.map(m => m.id)), [members]);

    // Options carry an `_label` (rendered in red by CustomMultiSelect's customOptions
    // renderer) when the employee already belongs to another group.
    const options = useMemo(() => employeeOptions
        .filter(opt => !assignedIds.has(opt.value))
        .map(opt => {
            const otherGroupId = membershipByUserId[opt.value];
            const inOther = otherGroupId && otherGroupId !== groupId;
            return {
                ...opt,
                _label: inOther && opt.groupName ? `${t('in')} ${opt.groupName}` : undefined,
            };
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }), [employeeOptions, assignedIds, membershipByUserId, groupId, t, locale]);

    const movedCount = pendingIds.filter(id => membershipByUserId[id] && membershipByUserId[id] !== groupId).length;

    const visibleMembers = search.trim()
        ? members.filter(m => m.fullname.toLowerCase().includes(search.trim().toLowerCase()))
        : members;

    // an employee removed from the group must not linger in the selection
    useEffect(() => {
        setSelectedIds(prev => {
            const next = prev.filter(id => assignedIds.has(id));
            return next.length === prev.length ? prev : next;
        });
    }, [assignedIds]);

    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    // select-all only covers what the search currently shows
    const visibleIds = visibleMembers.map(m => m.id);
    const visibleSelected = visibleIds.filter(id => selectedSet.has(id)).length;
    const allVisibleSelected = !!visibleIds.length && visibleSelected === visibleIds.length;

    const toggleAllVisible = (checked) => setSelectedIds(prev => (checked
        ? [...new Set([...prev, ...visibleIds])]
        : prev.filter(id => !visibleIds.includes(id))));

    const toggleOne = (id, checked) => setSelectedIds(prev => (checked
        ? [...prev, id]
        : prev.filter(selected => selected !== id)));

    const handleAdd = () => {
        if (!pendingIds.length) return;
        onAdd(pendingIds);
        setPendingIds([]);
    };

    return (
        <>
            <div className="alert alert-warning font-size-12 mb-3 perm-group-alert">
                <i className="fa fa-exclamation-triangle perm-group-alert-icon" aria-hidden="true" />
                <span>
                    <strong>{t('Adding an employee to a permission group replaces all their current permissions with the group permissions.')}</strong>
                    &nbsp;{t('An employee can belong to only one permission group.')}
                </span>
            </div>

            <div className="row gx-2 align-items-center mb-3">
                <div className="col-lg-7 col-md-8 mb-2 mb-md-0">
                    <CustomMultiSelect
                        options={options}
                        value={pendingIds}
                        onChange={setPendingIds}
                        customOptions
                        className="custom-multi-select-max-height-200"
                        overrideStrings={{
                            selectSomeItems: role === 'pm' ? t('Select project managers') : t('Select employees'),
                            allItemsAreSelected: t('All Employees are selected'),
                            search: t('Search'),
                            selectAll: t('Select All'),
                        }}
                    />
                </div>
                <div className="col-lg-5 col-md-4 d-flex align-items-center gap-2">
                    <Button fill onClick={handleAdd} disabled={!pendingIds.length}>
                        <i className="fa fa-plus me-1" aria-hidden="true" />
                        {t('Add')}{pendingIds.length ? ` (${pendingIds.length})` : ''}
                    </Button>
                    {movedCount ? (
                        <span className="font-size-12 text-danger">
                            <i className="fa fa-exchange me-1" aria-hidden="true" />
                            {movedCount} {t('will be moved from another permission group')}
                        </span>
                    ) : null}
                </div>
            </div>

            <div className="row gx-2 align-items-center mb-2">
                <div className="col-lg-7 col-md-6 d-flex align-items-center gap-2 mb-2 mb-md-0">
                    <span className="fw-bold font-size-12 text-dark">
                        {t('Assigned Employees')}
                        <span className="badge rounded-1 text-primary-color ms-2">{members.length}</span>
                    </span>
                    {selectedIds.length ? (
                        <>
                            <span className="font-size-12 text-dark">
                                {selectedIds.length} {t('selected')}
                            </span>
                            <Button danger fill sm onClick={() => onRemove(selectedIds)}>
                                <i className="fa fa-trash me-1" aria-hidden="true" />
                                {t('Remove')} ({selectedIds.length})
                            </Button>
                            <Button simple sm onClick={() => setSelectedIds([])}>{t('Clear')}</Button>
                        </>
                    ) : null}
                </div>
                {members.length > 5 ? (
                    <div className="col-lg-5 col-md-6">
                        <div className="conversation-search">
                            <input
                                type="text"
                                className="conversation-search-input"
                                placeholder={t('Search by employee name')}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                            <i className="fa fa-search conversation-search-icon" aria-hidden="true" />
                        </div>
                    </div>
                ) : null}
            </div>

            <div className="table-responsive perm-group-member-table-wrapper">
                <Table className="mb-0" hover>
                    <thead>
                        <tr>
                            <th width="5%" className="text-center">
                                <input
                                    type="checkbox"
                                    className="form-check-input mt-0 cursor-pointer"
                                    title={allVisibleSelected ? t('Clear selection') : t('Select All')}
                                    checked={allVisibleSelected}
                                    // some-but-not-all has no checked state of its own
                                    ref={el => { if (el) el.indeterminate = !!visibleSelected && !allVisibleSelected; }}
                                    disabled={!visibleIds.length}
                                    onChange={e => toggleAllVisible(e.target.checked)}
                                />
                            </th>
                            <th width="5%">{t('#')}</th>
                            <th width="38%" className="text-start">{t('Employee')}</th>
                            <th width="32%" className="text-start">{t('Department')}</th>
                            <th width="20%" className="text-center">{t('Actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!visibleMembers.length ? (
                            <tr>
                                <td colSpan={5} className="border-0">
                                    <div className="d-flex flex-column align-items-center justify-content-center text-center px-3 perm-group-empty">
                                        <span className="font-size-12 text-dark">
                                            {members.length
                                                ? t('No employees found')
                                                : t('No employee assigned yet. Add employees above to apply this group to them.')}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        ) : visibleMembers.map((member, index) => {
                            const noDepartment = member.employee_type === 'no_department';
                            return (
                                <tr key={member.id} className={selectedSet.has(member.id) ? 'table-active' : ''}>
                                    <td className="align-middle text-center">
                                        <input
                                            type="checkbox"
                                            className="form-check-input mt-0 cursor-pointer"
                                            checked={selectedSet.has(member.id)}
                                            onChange={e => toggleOne(member.id, e.target.checked)}
                                        />
                                    </td>
                                    <td className="align-middle font-size-12 text-dark">{index + 1}</td>
                                    <td className="align-middle text-start fw-bold font-size-12 text-dark">
                                        {member.fullname}
                                        {member.is_customised ? (
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip id={`tip-custom-${member.id}`}>{t('This employee has individual permission changes made after the permission group was applied.')}</Tooltip>}
                                            >
                                                <i className="fa fa-pencil text-warning ms-2" aria-hidden="true" />
                                            </OverlayTrigger>
                                        ) : null}
                                    </td>
                                    <td className="align-middle text-start font-size-12">
                                        {noDepartment ? (
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip id={`tip-nodept-${member.id}`}>{t('Roster permissions cannot be assigned to a user with No Department.')}</Tooltip>}
                                            >
                                                <span className="text-danger">{t('No Department')}</span>
                                            </OverlayTrigger>
                                        ) : (
                                            <span className="text-dark">
                                                {departmentLabels[member.employee_type] || member.employee_type || '-'}
                                            </span>
                                        )}
                                    </td>
                                    <td className="align-middle text-center text-nowrap">
                                        {member.is_customised ? (
                                            <OverlayTrigger placement="top" overlay={<Tooltip id={`tip-reapply-${member.id}`}>{t('Re-apply group permissions')}</Tooltip>}>
                                                <Button icon_sm fill me2 onClick={() => onReapply(member.id)}>
                                                    <i className="fa fa-refresh" aria-hidden="true" />
                                                </Button>
                                            </OverlayTrigger>
                                        ) : null}
                                        <OverlayTrigger placement="top" overlay={<Tooltip id={`tip-remove-${member.id}`}>{t('Remove from group')}</Tooltip>}>
                                            <Button icon_sm_delete fill onClick={() => onRemove(member.id)}>
                                                <img src={Delete} alt="delete" />
                                            </Button>
                                        </OverlayTrigger>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>
        </>
    );
};

export default PermissionGroupMembers;
